import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowRightOnRectangleIcon,
  PhotoIcon,
  VideoCameraIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { API_URL } from '../../config.ts';
import SiteSettingsForm from './SiteSettingsForm.tsx';

interface Project {
  id: number;
  title: string;
  description: string;
  shortDescription?: string;
  category: string;
  categoryDisplayName: string;
  status: string;
  statusDisplayName: string;
  featured: boolean;
  imageUrl?: string;
  images?: string[];
  videoUrl?: string;
  technologies: string[];
}

const CATEGORIES = [
  { value: 'ESP32', label: 'ESP32' },
  { value: 'STM32', label: 'STM32' },
  { value: 'ARDUINO', label: 'Arduino' },
  { value: 'PLC', label: 'ПЛК' },
  { value: 'IOT', label: 'IoT' },
  { value: 'AUTOMATION', label: 'Автоматизация' },
  { value: 'EMBEDDED', label: 'Встраиваемые системы' },
  { value: 'ROBOTICS', label: 'Робототехника' },
];

const STATUSES = [
  { value: 'PLANNING', label: 'Планирование' },
  { value: 'IN_PROGRESS', label: 'В разработке' },
  { value: 'COMPLETED', label: 'Завершен' },
  { value: 'ON_HOLD', label: 'Приостановлен' },
];

const STATUS_STYLES: Record<string, string> = {
  PLANNING: 'bg-gray-100 text-gray-700',
  IN_PROGRESS: 'bg-amber-100 text-amber-700',
  COMPLETED: 'bg-emerald-100 text-emerald-700',
  ON_HOLD: 'bg-rose-100 text-rose-700',
};

const emptyForm = {
  title: '',
  description: '',
  shortDescription: '',
  category: 'STM32',
  status: 'COMPLETED',
  technologies: '',
};

const ACCEPTED_IMAGE_TYPES = '.jpg,.jpeg,.png,.gif,.webp,.svg';
const ACCEPTED_VIDEO_TYPES = '.mp4,.webm,.mov';

const inputClass =
  'w-full px-3.5 py-2.5 border border-gray-300 rounded-lg shadow-sm text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition';

// Новые изображения (/images/uploads/...) отдаёт backend через /api — им нужен префикс API_URL.
// Старые изображения из DataLoader (/images/projects/...) — статика самого frontend, используются как есть.
const imageUrlFor = (path?: string) => {
  if (!path) return '';
  return path.startsWith('/images/uploads/') ? `${API_URL}${path}` : path;
};

const videoUrlFor = (path?: string) => {
  if (!path) return '';
  return path.startsWith('/videos/uploads/') ? `${API_URL}${path}` : path;
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [mainImageUrl, setMainImageUrl] = useState<string | null>(null);
  const [uploadingMain, setUploadingMain] = useState(false);
  const [galleryUrls, setGalleryUrls] = useState<string[]>([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);

  useEffect(() => {
    fetch(`${API_URL}/auth/me`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) {
          navigate('/admin');
          return;
        }
        setCheckingAuth(false);
        loadProjects();
      })
      .catch(() => navigate('/admin'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProjects = () => {
    setLoadingProjects(true);
    fetch(`${API_URL}/projects?size=100`)
      .then((res) => res.json())
      .then((data) => setProjects(data.content || []))
      .finally(() => setLoadingProjects(false));
  };

  const handleLogout = async () => {
    await fetch(`${API_URL}/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/admin');
  };

  const uploadImage = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/images/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(data?.message || 'Не удалось загрузить изображение');
    }
    return data.url as string;
  };

  const handleMainImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadError(null);
    setUploadingMain(true);
    try {
      setMainImageUrl(await uploadImage(file));
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setUploadingMain(false);
    }
  };

  const handleGallerySelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    setUploadError(null);
    setUploadingGallery(true);
    try {
      const uploaded: string[] = [];
      for (const file of files) {
        uploaded.push(await uploadImage(file));
      }
      setGalleryUrls((prev) => [...prev, ...uploaded]);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setUploadingGallery(false);
    }
  };

  const removeGalleryImage = (url: string) => {
    setGalleryUrls((prev) => prev.filter((u) => u !== url));
  };

  // Видео может быть большим (до нескольких ГБ), поэтому грузим через XHR —
  // fetch не даёт прогресс отправки без сложных обходов.
  const uploadVideo = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/videos/upload`);
      xhr.withCredentials = true;

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          setVideoUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        let data: { url?: string; message?: string } | null = null;
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          data = null;
        }
        if (xhr.status >= 200 && xhr.status < 300 && data?.url) {
          resolve(data.url);
        } else {
          reject(new Error(data?.message || 'Не удалось загрузить видео'));
        }
      };

      xhr.onerror = () => reject(new Error('Не удалось связаться с сервером'));

      xhr.send(formData);
    });
  };

  const handleVideoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadError(null);
    setUploadingVideo(true);
    setVideoUploadProgress(0);
    try {
      setVideoUrl(await uploadVideo(file));
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Ошибка загрузки видео');
    } finally {
      setUploadingVideo(false);
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setMainImageUrl(null);
    setGalleryUrls([]);
    setVideoUrl(null);
    setUploadingVideo(false);
    setVideoUploadProgress(0);
    setUploadError(null);
    setFormError(null);
    setEditingId(null);
  };

  const openAddForm = () => {
    resetForm();
    setShowForm(true);
  };

  const closeForm = () => {
    resetForm();
    setShowForm(false);
  };

  const startEdit = (project: Project) => {
    setForm({
      title: project.title,
      description: project.description,
      shortDescription: project.shortDescription || '',
      category: project.category,
      status: project.status,
      technologies: (project.technologies || []).join(', '),
    });
    setMainImageUrl(project.imageUrl || null);
    setGalleryUrls(project.images || []);
    setVideoUrl(project.videoUrl || null);
    setFormError(null);
    setUploadError(null);
    setEditingId(project.id);
    setShowForm(true);
  };

  const handleSubmitProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);

    const isEditing = editingId !== null;
    const url = isEditing ? `${API_URL}/projects/${editingId}` : `${API_URL}/projects`;

    try {
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title,
          description: form.description,
          shortDescription: form.shortDescription || undefined,
          category: form.category,
          status: form.status,
          imageUrl: mainImageUrl || undefined,
          images: galleryUrls,
          videoUrl: videoUrl || undefined,
          technologies: form.technologies
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean),
        }),
      });

      if (response.ok) {
        closeForm();
        loadProjects();
        return;
      }

      if (response.status === 401 || response.status === 403) {
        navigate('/admin');
        return;
      }

      const data = await response.json().catch(() => null);
      setFormError(data?.message || 'Не удалось сохранить проект. Проверьте обязательные поля.');
    } catch (err) {
      setFormError('Не удалось связаться с сервером');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить проект?')) return;
    const response = await fetch(`${API_URL}/projects/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (response.ok) {
      setProjects((prev) => prev.filter((p) => p.id !== id));
      if (editingId === id) closeForm();
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Проверка авторизации...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-10 max-w-6xl">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Панель администратора</h1>
            <p className="text-sm text-gray-500 mt-1">Управление проектами портфолио</p>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition"
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4" />
            Выйти
          </button>
        </div>

        <SiteSettingsForm />

        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200">
          <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">
              Проекты <span className="text-gray-400 font-normal">{!loadingProjects && `· ${projects.length}`}</span>
            </h2>
            <button
              onClick={openAddForm}
              className="inline-flex items-center gap-1.5 bg-indigo-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg shadow-sm hover:bg-indigo-700 transition"
            >
              <PlusIcon className="h-4 w-4" />
              Добавить проект
            </button>
          </div>

          {showForm && (
            <div
              className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-start justify-center p-4 z-50 overflow-y-auto"
            >
              <div className="bg-white rounded-2xl shadow-xl ring-1 ring-gray-200 w-full max-w-2xl my-8">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
                  <h3 className="text-base font-semibold text-gray-900">
                    {editingId !== null ? 'Редактирование проекта' : 'Новый проект'}
                  </h3>
                  <button
                    type="button"
                    onClick={closeForm}
                    className="text-gray-400 hover:text-gray-600 rounded-full p-1 hover:bg-gray-100 transition"
                    aria-label="Закрыть"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

              <form onSubmit={handleSubmitProject} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Название *</label>
                <input
                  type="text"
                  required
                  maxLength={200}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Краткое описание</label>
                <input
                  type="text"
                  maxLength={500}
                  value={form.shortDescription}
                  onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Описание *</label>
                <textarea
                  required
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className={inputClass}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Категория *</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className={inputClass}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Статус</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className={inputClass}
                  >
                    {STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Технологии (через запятую)</label>
                <input
                  type="text"
                  placeholder="STM32, C/C++, Bluetooth"
                  value={form.technologies}
                  onChange={(e) => setForm({ ...form, technologies: e.target.value })}
                  className={inputClass}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Главное изображение</label>
                  <label className="flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-gray-300 rounded-xl px-4 py-5 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/40 transition">
                    <PhotoIcon className="h-6 w-6 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      {uploadingMain ? 'Загрузка...' : 'Выбрать файл или перетащить'}
                    </span>
                    <input
                      type="file"
                      accept={ACCEPTED_IMAGE_TYPES}
                      onChange={handleMainImageSelect}
                      disabled={uploadingMain}
                      className="hidden"
                    />
                  </label>
                  {mainImageUrl && !uploadingMain && (
                    <div className="mt-3 relative inline-block">
                      <img
                        src={imageUrlFor(mainImageUrl)}
                        alt="Главное изображение"
                        className="h-20 w-20 object-cover rounded-lg shadow-sm ring-1 ring-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => setMainImageUrl(null)}
                        className="absolute -top-2 -right-2 bg-white text-gray-500 hover:text-red-600 rounded-full shadow ring-1 ring-gray-200 p-1"
                        aria-label="Убрать изображение"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Галерея</label>
                  <label className="flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-gray-300 rounded-xl px-4 py-5 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/40 transition">
                    <PhotoIcon className="h-6 w-6 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      {uploadingGallery ? 'Загрузка...' : 'Можно выбрать несколько файлов'}
                    </span>
                    <input
                      type="file"
                      accept={ACCEPTED_IMAGE_TYPES}
                      multiple
                      onChange={handleGallerySelect}
                      disabled={uploadingGallery}
                      className="hidden"
                    />
                  </label>
                  {galleryUrls.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {galleryUrls.map((url) => (
                        <div key={url} className="relative">
                          <img
                            src={imageUrlFor(url)}
                            alt="Галерея"
                            className="h-16 w-16 object-cover rounded-lg shadow-sm ring-1 ring-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeGalleryImage(url)}
                            className="absolute -top-2 -right-2 bg-white text-gray-500 hover:text-red-600 rounded-full shadow ring-1 ring-gray-200 p-1"
                            aria-label="Убрать изображение"
                          >
                            <XMarkIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Видео</label>
                <label className="flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-gray-300 rounded-xl px-4 py-5 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/40 transition">
                  <VideoCameraIcon className="h-6 w-6 text-gray-400" />
                  <span className="text-sm text-gray-500">
                    {uploadingVideo ? `Загрузка... ${videoUploadProgress}%` : 'Выбрать видеофайл (mp4/webm/mov)'}
                  </span>
                  <input
                    type="file"
                    accept={ACCEPTED_VIDEO_TYPES}
                    onChange={handleVideoSelect}
                    disabled={uploadingVideo}
                    className="hidden"
                  />
                </label>
                {uploadingVideo && (
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-indigo-600 h-1.5 rounded-full transition-all"
                      style={{ width: `${videoUploadProgress}%` }}
                    />
                  </div>
                )}
                {videoUrl && !uploadingVideo && (
                  <div className="mt-3 flex items-center gap-2">
                    <video
                      src={videoUrlFor(videoUrl)}
                      className="h-20 rounded-lg shadow-sm ring-1 ring-gray-200"
                      controls
                    />
                    <button type="button" onClick={() => setVideoUrl(null)} className="text-sm text-red-600 hover:text-red-800">
                      Убрать
                    </button>
                  </div>
                )}
              </div>

              {(formError || uploadError) && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {formError || uploadError}
                </p>
              )}

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-sm font-medium px-5 py-2.5 rounded-lg shadow-sm hover:bg-emerald-700 disabled:opacity-50 transition"
                >
                  {submitting ? 'Сохранение...' : editingId !== null ? 'Сохранить изменения' : 'Сохранить проект'}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  className="text-sm text-gray-500 hover:text-gray-800 transition"
                >
                  Отмена
                </button>
              </div>
              </form>
              </div>
            </div>
          )}

          <div className="p-6">
            {loadingProjects ? (
              <p className="text-gray-500 text-sm">Загрузка...</p>
            ) : projects.length === 0 ? (
              <p className="text-gray-500 text-sm">Проектов пока нет</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    className={`group h-full flex flex-col rounded-xl overflow-hidden ring-1 transition ${
                      editingId === project.id ? 'ring-2 ring-indigo-500' : 'ring-gray-200 hover:ring-gray-300'
                    }`}
                  >
                    <div className="h-32 shrink-0 bg-gray-100 flex items-center justify-center overflow-hidden">
                      {project.imageUrl ? (
                        <img
                          src={imageUrlFor(project.imageUrl)}
                          alt={project.title}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <PhotoIcon className="h-8 w-8 text-gray-300" />
                      )}
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <p className="font-medium text-gray-900 text-sm leading-snug line-clamp-2 min-h-[2.5rem]">{project.title}</p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="text-xs text-gray-500">{project.categoryDisplayName}</span>
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[project.status] || 'bg-gray-100 text-gray-700'}`}
                        >
                          {project.statusDisplayName}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
                        <button
                          onClick={() => startEdit(project)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-white hover:bg-indigo-600 px-2.5 py-1.5 rounded-lg transition"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                          Изменить
                        </button>
                        <button
                          onClick={() => handleDelete(project.id)}
                          className="inline-flex items-center justify-center text-gray-400 hover:text-white hover:bg-red-600 p-1.5 rounded-lg transition"
                          aria-label="Удалить проект"
                          title="Удалить проект"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
